import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import CustomIntakeModal from "../components/CustomIntakeModal";
import { useCart } from "../context/CartContext";

const CATEGORIES = ["All", "Fixed Candles", "Custom Hand-Poured"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [expanded, setExpanded] = useState({}); // product.id -> bool
  const [intakeProduct, setIntakeProduct] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const counts = useMemo(() => {
    const c = { All: products.length };
    for (const p of products) c[p.category] = (c[p.category] || 0) + 1;
    return c;
  }, [products]);

  const filtered = cat === "All" ? products : products.filter((p) => p.category === cat);

  const buy = (p) => {
    // Custom Hand-Poured items go through the intake form first (solo Stripe flow).
    if (p.category === "Custom Hand-Poured") {
      setIntakeProduct(p);
      return;
    }
    // All other products use the cart.
    addToCart(p, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="py-20 md:py-28 px-6 md:px-10 max-w-7xl mx-auto" data-testid="shop-page">
      <div className="overline mb-4">The Candle Catalog</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Fixed by fire. Sealed by spirit.</h1>
      <p className="mt-6 text-white/70 max-w-2xl text-lg">
        Every candle is hand-dressed, fixed, and prayed over at Lyon's Den by The Lion Chief —
        with sacred oils, herbs, and intention chosen for its purpose.
      </p>

      <div className="mt-12 flex flex-wrap gap-2" data-testid="shop-category-filter">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              cat === c
                ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                : "border-white/15 text-white/70 hover:border-[#D4AF37]/50"
            }`}
            data-testid={`shop-cat-${c.toLowerCase().replace(/\s+/g, "-")}-btn`}
          >
            {c} {counts[c] ? <span className="opacity-60 ml-1">({counts[c]})</span> : null}
          </button>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
        {filtered.map((p) => {
          const isOpen = !!expanded[p.id];
          return (
            <article
              key={p.id}
              className="group bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/40 transition-colors flex flex-col"
              data-testid={`product-card-${p.id}`}
            >
              <div className="aspect-square overflow-hidden bg-[#111]">
                <img
                  src={p.image_url}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="overline text-[#D4AF37]">{p.category}</div>
                <h3 className="font-serif text-xl mt-2" data-testid={`product-${p.id}-name`}>{p.name}</h3>
                {p.tagline ? (
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 mt-1" data-testid={`product-${p.id}-tagline`}>
                    {p.tagline}
                  </div>
                ) : null}
                <p className="text-sm text-white/55 mt-3 leading-relaxed">{p.description}</p>

                {Array.isArray(p.best_for) && p.best_for.length > 0 && (
                  <div className="mt-4">
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                      className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white transition-colors"
                      data-testid={`product-${p.id}-bestfor-toggle`}
                    >
                      {isOpen ? "− Hide Best For" : "+ Best For"}
                    </button>
                    {isOpen && (
                      <ul className="mt-3 space-y-1.5 text-sm text-white/70" data-testid={`product-${p.id}-bestfor-list`}>
                        {p.best_for.map((b, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#D4AF37]">•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-5 flex items-center justify-between">
                  <div className="font-serif text-2xl" data-testid={`product-${p.id}-price`}>
                    ${p.price.toFixed(2)}
                  </div>
                  <button
                    onClick={() => buy(p)}
                    disabled={p.inventory === 0}
                    className="bg-[#8B0000] hover:bg-[#A50000] text-white px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] disabled:opacity-50"
                    data-testid={`product-${p.id}-buy-btn`}
                  >
                    {p.inventory === 0 ? "Sold Out" : p.category === "Custom Hand-Poured" ? "Begin Intake" : "Add to Cart"}
                  </button>
                </div>

                {p.disclaimer ? (
                  <p className="mt-4 text-[10px] leading-relaxed text-white/35" data-testid={`product-${p.id}-disclaimer`}>
                    {p.disclaimer}
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-white/50 text-center py-20">No products in this category yet.</div>
        )}
      </div>

      {intakeProduct && (
        <CustomIntakeModal product={intakeProduct} onClose={() => setIntakeProduct(null)} />
      )}
    </div>
  );
}
